import { z } from "zod";
import { IdSchema } from "./primitives.js";
/**
 * FDB MedKnowledge Canada content served by the database (fdb schema,
 * 20260930144302/20260930144304): medication and allergen search, interaction
 * monographs, and prescription screening. `contentStatus: "synthetic"` marks
 * the FDB-shaped test release used until licensed files are delivered; the UI
 * must say so wherever the content appears.
 */
export const FdbReleaseSchema = z
    .object({
    buildDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    contentStatus: z.enum(["synthetic", "licensed"]),
    bundle: z.string().min(1).max(120),
})
    .strict();
/** FDB requires its disclaimer and copyright on every screen and printout. */
export const FdbAttributionSchema = z
    .object({
    disclaimer: z.string().min(1),
    copyright: z.string().min(1),
    drugProductDatabase: z.string().min(1).nullable(),
})
    .strict();
export const FdbSearchQuerySchema = z
    .object({
    query: z.string().trim().min(2).max(120),
    limit: z.coerce.number().int().min(1).max(50).default(20),
})
    .strict();
export const FdbMedicationSchema = z
    .object({
    medid: z.number().int().positive(),
    code: z.string().regex(/^\d{1,9}$/),
    codeSystem: z.literal("FDB_MEDID"),
    display: z.string().min(1).max(240),
    tallManDisplay: z.string().min(1).max(240).nullable(),
    strength: z.string().max(40).nullable(),
    strengthUnit: z.string().max(40).nullable(),
    gcnSeqno: z.number().int().positive().nullable(),
    dins: z.array(z.string().regex(/^\d{8}$/)),
    /** Complete labels verified from FDB generic links and matching formulation metadata. */
    genericEquivalentDisplays: z.array(z.string().min(1).max(240)).max(2).optional(),
})
    .strict();
export const FdbMedicationSearchResponseSchema = z
    .object({
    query: z.string(),
    release: FdbReleaseSchema.nullable(),
    medications: z.array(FdbMedicationSchema).max(50),
    /** Missing on older servers: automatic selection requires explicit completeness. */
    hasMore: z.boolean().optional(),
    attribution: FdbAttributionSchema.nullable(),
})
    .strict();
export const FdbAllergenSchema = z
    .object({
    code: z.string().regex(/^\d{1,9}$/),
    codeSystem: z.enum(["FDB_HIC_SEQN", "FDB_DAM_ALRGN_GRP"]),
    display: z.string().min(1).max(240),
    kind: z.enum(["ingredient", "group"]),
})
    .strict();
export const FdbAllergenSearchResponseSchema = z
    .object({
    query: z.string(),
    release: FdbReleaseSchema.nullable(),
    allergens: z.array(FdbAllergenSchema).max(50),
})
    .strict();
export const FdbInteractionMonographSchema = z
    .object({
    monographId: z.number().int().positive(),
    title: z.string().nullable(),
    lines: z.array(z.object({ section: z.string().nullable(), text: z.string().nullable() }).strict()),
    release: FdbReleaseSchema.nullable(),
    attribution: FdbAttributionSchema.nullable(),
})
    .strict();
/** Structured directions: what dose-range checking needs from a sig. */
export const StructuredSigSchema = z
    .object({
    doseAmount: z.number().positive().max(100_000),
    doseUnit: z.string().trim().min(1).max(20),
    frequencyPerDay: z.number().positive().max(48),
    durationDays: z.number().int().positive().max(3650).optional(),
})
    .strict();
export const FdbScreeningFindingSchema = z
    .object({
    key: z.string().min(1).max(200),
    kind: z.enum(["interaction", "allergy", "duplicate_therapy", "dose"]),
    severity: z.enum(["contraindicated", "severe", "moderate", "undetermined"]),
    displayAction: z.enum(["halt", "interrupt", "informative", "surveillance"]).nullable(),
    title: z.string().min(1),
    detail: z.string().min(1),
    withDisplay: z.string().optional(),
    monographId: z.number().int().positive().optional(),
    requiresOverride: z.boolean(),
})
    .strict();
export const FdbUnscreenedItemSchema = z
    .object({
    kind: z.enum(["medication", "allergy", "dose"]),
    id: IdSchema.optional(),
    display: z.string(),
    reason: z.string().min(1).max(80),
})
    .strict();
export const FdbScreeningSchema = z
    .object({
    status: z.enum(["screened", "not_screened"]),
    reason: z.enum(["uncoded_medication", "no_release", "medication_not_in_release"]).optional(),
    release: FdbReleaseSchema.nullable(),
    medication: z
        .object({
        medid: z.number().int().positive().nullable(),
        gcnSeqno: z.number().int().positive(),
        display: z.string().nullable(),
    })
        .strict()
        .optional(),
    findings: z.array(FdbScreeningFindingSchema),
    unscreened: z.array(FdbUnscreenedItemSchema),
    requiresOverride: z.boolean(),
    screenedAt: z.string().optional(),
    attribution: FdbAttributionSchema.nullable().optional(),
})
    .strict();
/**
 * A medication listed before the screened one on the same prescription
 * (RX-3). It counts like a chart medication for interaction and
 * duplicate-therapy checks, so a pair on one prescription alerts once, on the
 * later medication.
 */
export const FdbCoPrescribedMedicationSchema = z
    .object({
    medicationDisplay: z.string().trim().min(1).max(240),
    medicationCode: z.string().trim().min(1).max(64).optional(),
    medicationCodeSystem: z.enum(["FDB_MEDID", "DIN", "RxNorm", "local"]).optional(),
})
    .strict();
export const FdbScreeningRequestSchema = z
    .object({
    patientId: IdSchema,
    prescription: z
        .object({
        medicationDisplay: z.string().trim().min(1).max(240),
        medicationCode: z.string().trim().min(1).max(64).optional(),
        medicationCodeSystem: z.enum(["FDB_MEDID", "DIN", "RxNorm", "local"]).optional(),
        structuredSig: StructuredSigSchema.optional(),
    })
        .strict(),
    /** The medications listed before this one; a prescription lists at most ten. */
    coPrescribed: z.array(FdbCoPrescribedMedicationSchema).max(9).optional(),
})
    .strict();
export const FdbOverrideReasonCodeSchema = z.enum([
    "benefit_outweighs_risk",
    "tolerated_previously",
    "monitoring_in_place",
    "dose_intended",
    "other",
]);
export const FdbScreeningOverrideSchema = z
    .object({
    findingKey: z.string().min(1).max(200),
    reasonCode: FdbOverrideReasonCodeSchema,
    reasonText: z.string().trim().max(500).optional(),
})
    .strict()
    .refine((override) => override.reasonCode !== "other" || (override.reasonText ?? "").length >= 3, { message: "An 'other' override needs a written reason", path: ["reasonText"] });
//# sourceMappingURL=fdb-medknowledge.js.map